/* eslint-disable better-mutation/no-mutation */

/**
 * HOW IT WORKS
 *
 * urban airship has something called channels. A tradera member subscribes to a channel to get push notifications.
 * each channel has tags that indicate what kind of push notifications the member wants to receive. In the case of the wish list
 * reminder that particular setting subscribes the user to 2 different notifications: reminder 15 minutes before auction ends
 * and reminder 3 minutes before the auction ends. In order to be able to have 1 push notification setting that subscribes
 * to multiple push notifications at tradera we call these tag groups.
 *
 *
 * Urban airship docs:
 * https://docs.airship.com/reference/libraries/web-notify-sdk/latest/index.html
 *
 * web_version_2:
 * Version 1 of push notifications was just targetted at wish list reminders, 3 min and 15 min. When this release was done
 * the code did subscribe the user to all notifications even though the user only wanted to signup for wish list reminders.
 * Since the app push notifier only sent wishlist reminders for web push it was not a problem that the user was subscribed to all
 * kinds of notifications. In the next release when tradera wanted to offer all sorts of push notifications but wanted the users
 * to on their own sign up then we had to update all the current subscriptions. Since this is not possible via the urban airship
 * to update channels via the REST API and then see the updated reflected in the Web SDK we had to introduce the tag web_version_2
 * that indicates if the user has updated their push notification settings.
 *
 * opt-in dialog sample usage:
 * import uiActions from 'tradera-state/ui/actions';
 * import { MODAL_TYPES } from 'tradera-constants/push-notifications';
 *
 * uiActions.openNotificationOptinModal(
 *      MODAL_TYPES.wishlistReminders
 * )
 *
 */

import { createSlice } from '@reduxjs/toolkit';
import { logger } from 'packages/logger';
import {
    CHANNEL_TAG_GROUPS,
    MODAL_TYPES
} from 'tradera-state/push-notifications/constants';
import { localStore } from 'utils/storage';
import { STORAGE_KEYS } from 'tradera-constants/storage';
import {
    getUrbanSdk,
    getTagGroupsFromUrbanAirship,
    isPushEnabled
} from './helpers';

const pushNotificationsSlice = createSlice({
    name: 'pushNotifications',
    initialState: {
        enabled: false,
        initDone: false,
        pushSupported: false,
        optedIn: false,
        tagGroups: {
            wishlistReminders: false,
            buyingListingOutbid: false,
            buyingListingWon: false,
            sellingListingFirstBid: false,
            sellingListingAllBids: false,
            sellingListingSold: false,
            sellingPaymentReceived: false
        },
        shownModals: {
            [MODAL_TYPES.wishlistReminders]: false,
            [MODAL_TYPES.default]: false,
            [MODAL_TYPES.selling]: false
        }
    },
    reducers: {
        setInitDone(state) {
            state.initDone = true;
        },
        setPushSupported(state, actions) {
            state.pushSupported = actions.payload;
            if (!state.pushSupported) {
                state.optedIn = false;
            }
        },
        setOptedInValue(state, action) {
            state.optedIn = action.payload;
        },
        setTagGroupsValue(state, action) {
            const tagGroups = action.payload;
            state.tagGroups = { ...state.tagGroups, ...tagGroups };
        },
        setShownModalTrue(state, action) {
            const modalType = action.payload;
            state.shownModals[modalType] = true;
            localStore.setItem(
                STORAGE_KEYS.WEB_PUSH_SHOWN_MODALS,
                state.shownModals
            );
        },
        setAllShownModals(state, action) {
            const shownModals = action.payload;
            state.shownModals = shownModals;
            localStore.setItem(
                STORAGE_KEYS.WEB_PUSH_SHOWN_MODALS,
                state.shownModals
            );
        },
        setEnabled(state, action) {
            const enabled = action.payload;
            state.enabled = enabled;
        }
    }
});

const actions = pushNotificationsSlice.actions;
export const { setShownModalTrue } = actions;
export default pushNotificationsSlice.reducer;

/**
 * this should be called as soon as the store is mounted
 */
export const init = () => async (dispatch, getState) => {
    let sdk = null;
    try {
        sdk = await getUrbanSdk();
    } catch (error) {
        // not able to init sdk
    }
    dispatch(
        actions.setPushSupported(
            sdk !== null && sdk.canRegister && sdk.isSupported
        )
    );

    if (sdk !== null && sdk.channel !== null) {
        dispatch(actions.setOptedInValue(sdk.channel.optedIn));

        // has this user been upgraded to version 2?
        if (!sdk.channel.tags.has('web_version_2')) {
            await sdk.channel.tags.set([
                'wishlist_reminder_3_min_optin',
                'wishlist_reminder_15_min_optin',
                'web_version_2'
            ]);
        }
        const tagGroups = await getTagGroupsFromUrbanAirship();
        dispatch(actions.setTagGroupsValue(tagGroups));
    }
    dispatch(actions.setEnabled(sdk !== null && isPushEnabled(getState)));

    // load modal shown settings from local store
    const shownModals = localStore.getItem(STORAGE_KEYS.WEB_PUSH_SHOWN_MODALS);
    if (shownModals !== null) {
        dispatch(actions.setAllShownModals(shownModals));
    }
    dispatch(actions.setInitDone());
};

export const optIn = () => async (dispatch, getState) => {
    try {
        dispatch(actions.setOptedInValue(true));
        const memberId = getState().member.memberId;
        const sdk = await getUrbanSdk();
        const channel = await sdk.register();
        await channel.namedUser.set(memberId.toString());
        return channel;
    } catch (error) {
        dispatch(actions.setOptedInValue(false));
        return null;
    }
};

export const optOut = () => async dispatch => {
    try {
        dispatch(actions.setOptedInValue(false));
        const sdk = await getUrbanSdk();
        if (sdk !== null && sdk.channel !== null) {
            await sdk.channel.optOut();
        }
    } catch (error) {
        dispatch(actions.setOptedInValue(true));
        return null;
    }
};

const saveTagsToUrbanAirshipAndUpdateState = async (
    channel,
    tagGroups,
    tagGroupValue,
    dispatch,
    getState
) => {
    // check what tag groups to set and not set
    const tagGroupsToSet = { ...getState().pushNotifications.tagGroups };
    for (let t of tagGroups) {
        tagGroupsToSet[t] = tagGroupValue;
    }

    // get the actual tags that we want to get push notifications for and
    // register these tags in urban airship
    let channelTags = ['web_version_2'];
    for (const [group, enabled] of Object.entries(tagGroupsToSet)) {
        if (enabled) {
            channelTags = [...channelTags, ...CHANNEL_TAG_GROUPS[group]];
        }
    }

    dispatch(actions.setTagGroupsValue(tagGroupsToSet));
    await channel.tags.set(channelTags);
};

export const addTagGroups = tagGroupsToAdd => async (dispatch, getState) => {
    try {
        // no sdk? then do nothing
        const sdk = await getUrbanSdk();
        if (sdk === null) {
            return;
        }

        let channel = sdk.channel;
        if (channel === null) {
            channel = await dispatch(optIn());
            if (channel === null) {
                dispatch(actions.setPushSupported(false));
                // seems user clicked on block notifications in the browser, abort
                return;
            }
        }

        saveTagsToUrbanAirshipAndUpdateState(
            channel,
            tagGroupsToAdd,
            true,
            dispatch,
            getState
        );
    } catch (error) {
        logger(error);
    }
};

export const removeTagGroups = tagGroupsToRemove => async (
    dispatch,
    getState
) => {
    try {
        // no sdk and no subscription? then do nothing
        const sdk = await getUrbanSdk();
        if (sdk === null || sdk.channel === null) {
            return;
        }
        saveTagsToUrbanAirshipAndUpdateState(
            sdk.channel,
            tagGroupsToRemove,
            false,
            dispatch,
            getState
        );
    } catch (error) {
        logger(error);
    }
};
